import { useEffect, useRef, useState } from 'react';

/**
 * Holds `isLoading=true` for at least `minMs` after it first turns true, so
 * skeletons never strobe on fast (<minMs) responses. If the upstream resolves
 * before minMs elapses, the hook still returns true until the timer fires.
 *
 * Only counts a "load" once per mount cycle. If the upstream goes from false →
 * true → false → true (e.g. refetch), only the FIRST true triggers the floor.
 * Subsequent refetches return the upstream value directly so background
 * refreshes don't re-introduce a skeleton.
 *
 * Default 300ms matches the perceptual threshold where users start to register
 * "something was there." Anything shorter reads as a flash.
 */
export function useMinimumLoading(isLoading: boolean, minMs = 300): boolean {
  const [floored, setFloored] = useState(isLoading);
  const startedAt = useRef<number | null>(isLoading ? Date.now() : null);
  const hasShownData = useRef(false);

  useEffect(() => {
    if (isLoading) {
      if (hasShownData.current) {
        // Subsequent load (refetch). Don't re-introduce skeleton.
        setFloored(false);
        return;
      }
      if (startedAt.current === null) startedAt.current = Date.now();
      setFloored(true);
      return;
    }

    // Upstream resolved. Compute elapsed and either release now or schedule.
    const elapsed = startedAt.current === null ? minMs : Date.now() - startedAt.current;
    const remaining = Math.max(0, minMs - elapsed);

    if (remaining === 0) {
      hasShownData.current = true;
      setFloored(false);
      return;
    }

    const handle = window.setTimeout(() => {
      hasShownData.current = true;
      setFloored(false);
    }, remaining);
    return () => window.clearTimeout(handle);
  }, [isLoading, minMs]);

  return floored;
}
